import { connect } from 'react-redux';
import { change } from 'redux-form';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { AgeType } from '@eti/schema-types';
import { useMemo, useState } from 'react';
import { getProduct } from '../../single-choice/utils/singleChoiceTransformer';
import ProductLayout from '../../common/main-layout/ProductLayout';
import { useProductData } from '../../common/hook/useProductData';
import { getSelected } from '../../single-choice/utils/selection';
import { AIRPORT_TRANSFER } from '../../products';
import { ProductCardLayoutType } from '../../common/models/product';
import { standardGrid } from '../../common/main-layout/styles';
import { getOperatorList, isDistribusionChoice, NoneOption, sortOperatorList, withoutNulls } from './utils';
import AirportTransferSelection from './AirportTransferSelection';
import AirportTransferDrawer from './AirportTransferDrawer';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const airportTransferGrid = css`
  ${standardGrid}

  .illustration {
    grid-area: 1 / 1 / 3 / 2;
    min-height: calc(8.5rem + 1px);
  }
`;
const {
  name: PRODUCT_NAME,
  id: PRODUCT_ID
} = AIRPORT_TRANSFER;
const sharedTexts = {
  abstractContent: 'airportTransferAbstract',
  readMoreContent: 'airportTransferReadMoreModalContent',
  title: 'airportTransferTitle',
  upSell: 'airportTransferUpSellText',
  cheapest: 'airportTransferCardCheapest',
  fastest: 'airportTransferCardFastest',
  seeDetails: 'airportTransferCardSeeDetails',
  reject: 'airportTransferCardReject',
  transferTime: 'airportTransferCardTransferTime',
  transportAvailable: 'airportTransferCardTransportAvailable',
  from: 'airportTransferCardFrom',
  fromAirport: 'airportTransferCardFromAirport',
  to: 'airportTransferCardTo',
  toAirport: 'airportTransferCardToAirport',
  transfer: 'airportTransferCardTransfer',
  bus: 'airportTransferCardBus',
  train: 'airportTransferCardTrain',
  hour: 'airportTransferCardHour',
  hours: 'airportTransferCardHours',
  minute: 'airportTransferCardMinute',
  minutes: 'airportTransferCardMinutes',
  perPerson: 'airportTransferCardPerPerson',
  tickets: 'airportTransferCartTickets',
  availableTickets: 'airportTransferDrawerAvailableTickets',
  ticketDetails: 'airportTransferDrawerTicketDetails',
  available: 'airportTransferDrawerAvailable',
  unavailable: 'airportTransferDrawerUnavailable',
  termsAndConditions: 'airportTransferDrawerTermsAndConditions',
  transferBetween: 'airportTransferDrawerTransferBetween',
  andTranslation: 'airportTransferDrawerAndTranslation',
  operatingHours: 'airportTransferDrawerOperatingHours',
  operatingHoursContent: 'airportTransferDrawerOperatingHoursContent',
  conditions: 'airportTransferDrawerConditions',
  conditionsContent: 'airportTransferDrawerConditionsContent',
  pleaseNote: 'airportTransferDrawerPleaseNote',
  pleaseNoteContent: 'airportTransferDrawerPleaseNoteContent',
  validAnyDeparture: 'airportTransferCardValidAnyDeparture',
  validityInEmail: 'airportTransferCardValidityInEmail',
  validFor: 'airportTransferCardValidFor',
  validUntil: 'airportTransferCardValidUntil',
  fromPurchase: 'airportTransferCardFromPurchase',
  day: 'airportTransferCardDay',
  days: 'airportTransferCardDays',
  month: 'airportTransferCardMonth',
  months: 'airportTransferCardMonths',
  departs: 'airportTransferCardDeparts',
  validityBookedSlotBullet: 'airportTransferDrawerValidityBookedSlotBullet',
  validityInEmailBullet: 'airportTransferDrawerValidityInEmailBullet',
  bookingFinalBullet: 'airportTransferDrawerBookingFinalBullet',
  operatingHoursFallbackContent: 'airportTransferDrawerOperatingHoursFallbackContent'
};
const AirportTransfer = ({
  className,
  dispatch,
  form,
  isSelected,
  layoutType = ProductCardLayoutType.Main,
  order,
  productPosition,
  sellSpecification
}) => {
  var _productData$extraPro, _productData$extraPro2, _productData$extraPro3;
  const {
    t
  } = useTranslation();
  const sortedOperatorList = useMemo(() => {
    const product = getProduct(PRODUCT_ID, order, 'none');
    if (!product) {
      return null;
    }
    const operatorList = getOperatorList(t, isSelected, product, sellSpecification);
    if (!operatorList || operatorList.length === 0) {
      return null;
    }
    return sortOperatorList(operatorList);
  }, [t, isSelected, order, sellSpecification]);
  const {
    productData,
    rootElement,
    translation
  } = useProductData({
    className,
    form,
    isSelected,
    layoutType,
    order,
    productId: PRODUCT_ID,
    productName: PRODUCT_NAME,
    productPosition,
    sellSpecification,
    texts: sharedTexts
  });
  const [drawerOperator, setDrawerOperator] = useState(null);
  if (!(productData && sortedOperatorList)) {
    return null;
  }
  const isSingleOperator = sortedOperatorList.length === 1;
  const handleChange = e => {
    var _flatMap$filter, _ref, _sellSpecification$ex, _sellSpecification$ex2;
    const value = e === null || e === void 0 ? void 0 : e.target.value;
    const hasSelection = Boolean(value && value !== NoneOption && (sellSpecification === null || sellSpecification === void 0 ? void 0 : sellSpecification.externalChoices));
    const travelerPriceList = (_flatMap$filter = (_ref = hasSelection ? sellSpecification === null || sellSpecification === void 0 || (_sellSpecification$ex = sellSpecification.externalChoices) === null || _sellSpecification$ex === void 0 ? void 0 : _sellSpecification$ex.filter(choice => choice.externalChoiceId === Number(value)) : [sellSpecification === null || sellSpecification === void 0 || (_sellSpecification$ex2 = sellSpecification.externalChoices) === null || _sellSpecification$ex2 === void 0 ? void 0 : _sellSpecification$ex2[0]]) === null || _ref === void 0 ? void 0 : _ref.flatMap(choice => isDistribusionChoice(choice) ? choice.pricePerTravelers : []).filter(withoutNulls())) !== null && _flatMap$filter !== void 0 ? _flatMap$filter : [];
    dispatch(change(form, PRODUCT_NAME, {
      value,
      selectionTravelerExternalChoice: travelerPriceList.map(traveler => {
        return {
          travelerId: traveler.travelerId,
          choiceId: value,
          price: hasSelection ? traveler.price.price.value : 0
        };
      })
    }));
    const {
      from,
      to,
      tickets
    } = translation;
    if (hasSelection) {
      var _order$travelers$filt, _order$travelers, _selectedOperator$tra;
      const selectedOperator = sortedOperatorList === null || sortedOperatorList === void 0 ? void 0 : sortedOperatorList.find(op => String(op.externalChoiceId) === value);
      const nonInfantCount = (_order$travelers$filt = (_order$travelers = order.travelers) === null || _order$travelers === void 0 ? void 0 : _order$travelers.filter(traveler => traveler.ageType !== AgeType.Inf).length) !== null && _order$travelers$filt !== void 0 ? _order$travelers$filt : 0;
      const airportList = (_selectedOperator$tra = selectedOperator === null || selectedOperator === void 0 ? void 0 : selectedOperator.transferBoundData.map(bound => ({
        airportName: `${bound.departureStation} – ${bound.arrivalStation}`,
        numberOfPass: nonInfantCount,
        screenReaderText: `${from} ${bound.departureStation}, ${to} ${bound.arrivalStation}`
      }))) !== null && _selectedOperator$tra !== void 0 ? _selectedOperator$tra : [];
      dispatch(change(form, `${PRODUCT_NAME}.cartBreakDown`, {
        hasAvailability: true,
        airportList,
        passLabel: tickets
      }));
    } else {
      dispatch(change(form, `${PRODUCT_NAME}.cartBreakDown`, null));
    }
  };
  return /*#__PURE__*/_jsxs(ProductLayout, {
    coreComponentsRenderConfig: ['Illustration', 'Header', 'Body'],
    customGrid: isSingleOperator ? undefined : airportTransferGrid,
    productData: productData,
    ref: rootElement,
    children: [/*#__PURE__*/_jsx(AirportTransferSelection, {
      handleOpenDrawer: setDrawerOperator,
      handleRadioChange: handleChange,
      labelNo: (_productData$extraPro = productData.extraProps) === null || _productData$extraPro === void 0 ? void 0 : _productData$extraPro.labelNo,
      labelYes: (_productData$extraPro2 = productData.extraProps) === null || _productData$extraPro2 === void 0 ? void 0 : _productData$extraPro2.labelYes,
      operatorList: sortedOperatorList,
      productName: PRODUCT_NAME,
      selectOneLabel: (_productData$extraPro3 = productData.extraProps) === null || _productData$extraPro3 === void 0 ? void 0 : _productData$extraPro3.selectOneLabel,
      translation: translation
    }), drawerOperator && /*#__PURE__*/_jsx(AirportTransferDrawer, {
      handleClose: () => setDrawerOperator(null),
      operator: drawerOperator,
      translation: translation
    })]
  });
};
const mapStateToProps = (state, {
  form
}) => {
  const productName = AIRPORT_TRANSFER.name;
  return {
    isSelected: getSelected(productName, form)(state)
  };
};
const mapDispatchToProps = dispatch => ({
  dispatch
});
export default connect(mapStateToProps, mapDispatchToProps)(AirportTransfer);
//# sourceMappingURL=AirportTransfer.js.map